/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import {
    Box,
    Button,
    Paper,
    Stack,
    Typography, Skeleton, Grid
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Heading from "../heading";
import CtaButton from "../buttonWithArrow";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getTopActivites } from '../../redux/slices/common';

/* === mock data === */
const LEFT = [
    { title: "Attraction", img: "/img/top_activites/attraction.svg", href: "/activities/activities?type=Attraction", reviews: 212 },
    { title: "Dinner Cruise", img: "/img/top_activites/dinner.svg", href: "/activities/activities?type=Dinner%20Cruise", reviews: 100 },
    { title: "Kids Attraction", img: "/img/top_activites/kids.svg", href: "/activities/activities?type=Kids%20Attraction", reviews: 100 },
];

const RIGHT = [
    { title: "Safari", img: "/img/top_activites/jetski.svg", href: "/activities/activities?type=Safari", reviews: 760 },
    { title: "Water Adventure", img: "/img/top_activites/jetcar.svg", href: "/activities/activities?type=Water%20Adventure", reviews: 60 },
    { title: "Ski Dubai", img: "/img/top_activites/skidubai.svg", href: "/activities/activities?type=Attraction", reviews: 32 },
];

const TopActivitiesSkeleton = () => {
    return (
        <Box sx={{ py: { xs: 4, md: 8 } }}>
            {/* Heading */}
            <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
                <Skeleton
                    variant="text"
                    width={260}
                    height={22}
                    sx={{ mx: "auto", mb: 1 }}
                />
                <Skeleton
                    variant="text"
                    width={220}
                    height={40}
                    sx={{ mx: "auto", mb: 1 }}
                />
                <Skeleton
                    variant="rectangular"
                    width={130}
                    height={6}
                    sx={{ mx: "auto", borderRadius: 3 }}
                />
            </Box>

            <Grid
                container
                spacing={{ xs: 3, md: 4 }}
                alignItems="center"
                justifyContent="center"
            >
                {/* Left column cards */}
                <Grid item xs={12} md={3}>
                    <Stack spacing={3}>
                        {[0, 1, 2].map((i) => (
                            <Box key={i}>
                                <Skeleton
                                    variant="rounded"
                                    width="100%"
                                    height={140}
                                    sx={{ borderRadius: 4, mb: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="60%"
                                    height={22}
                                    sx={{ mx: "auto" }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Grid>

                {/* Center illustration + button */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Skeleton
                            variant="rounded"
                            width="100%"
                            height={360}
                            sx={{ maxWidth: 480, borderRadius: 6 }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Skeleton
                            variant="rounded"
                            width={140}
                            height={44}
                            sx={{ borderRadius: 999 }}
                        />
                    </Box>
                </Grid>

                {/* Right column cards */}
                <Grid item xs={12} md={3}>
                    <Stack spacing={3}>
                        {[0, 1, 2].map((i) => (
                            <Box key={i}>
                                <Skeleton
                                    variant="rounded"
                                    width="100%"
                                    height={140}
                                    sx={{ borderRadius: 4, mb: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="70%"
                                    height={22}
                                    sx={{ mx: "auto" }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default function TopActivities() {
    const dispatch = useDispatch();
    const { topActivitesRes } = useSelector((state) => state.common);
    const [loader, setLoader] = useState(true);
    const [datas, setDatas] = useState([]);

    useEffect(() => {
        dispatch(getTopActivites());
        setLoader(true);
    }, [dispatch])

    useEffect(() => {
        if (topActivitesRes) {
            setLoader(false);
            if (topActivitesRes?.status === 1) {
                setDatas(topActivitesRes?.responseData ?? []);
            } else {
                setDatas([]);
            }
        }
    }, [topActivitesRes])

    if (loader) {
        return <TopActivitiesSkeleton />;
    }

    return (
        <Box
            sx={{
                pt: { xs: 6, md: 4 },
                backgroundImage: "url('/img/banner/grid.svg')",
            }}
        >
            {/* Heading */}
            <Heading title={datas?.subtitle} subtitle={datas?.title} mb={4} />

            {/* 3-column layout */}
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: "auto",
                    // px: { xs: 2, sm: 3, md: 4 },
                    display: { xs: "block", md: "grid" },
                    gridTemplateColumns: { md: "1fr auto 1fr" },
                    // columnGap: { md: 4 },
                    rowGap: { xs: 3, md: 0 },
                    alignItems: "center",
                }}
            >
                {/* left column */}
                <Box sx={{ gridColumn: { md: 1 } }}>
                    <SideList items={datas?.left} />
                </Box>

                {/* center hero + CTA */}
                <Box
                    sx={{
                        gridColumn: { md: "2 / span 1" },
                        display: { xs: "none", md: "flex" },
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: { md: 560, lg: 620 },
                            maxWidth: "100%",
                            aspectRatio: "7 / 6",
                        }}
                    >
                        <Image
                            src={`${process.env.IMAGE_BASE_URL}/${datas?.bannerImage}`}
                            alt={datas?.title}
                            fill
                            priority
                            sizes="(max-width: 1200px) 80vw, 560px"
                            style={{ objectFit: "contain" }}
                        />
                    </Box>
                    {/* <CtaButton cta={{ label: datas?.viewButton?.label, href: datas?.viewButton?.href }} /> */}
                    <CtaButton cta={{ label: datas?.viewButton?.label }} />
                </Box>

                {/* right column */}
                <Box sx={{ gridColumn: { md: 3 } }}>
                    <SideList items={datas?.right} />
                </Box>
            </Box>

            {/* Mobile CTA under content (centered) */}
            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    justifyContent: "center",
                    mt: 3,
                }}
            >
                {/* <CtaButton cta={{ label: datas?.viewButton?.label, href: datas?.viewButton?.href }} /> */}
                <CtaButton cta={{ label: datas?.viewButton?.label }} />
            </Box>
        </Box>
    );
}

/* === side cards (left/right) === */
function SideList({ items }) {
    return (
        <Stack spacing={{ xs: 2, md: 2 }} sx={{ width: "100%" }}>
            {items.map((it) => (
                <ActivityThumb key={it.title} {...it} />
            ))}
        </Stack>
    );
}

function ActivityThumb({
    title,
    img,
    reviews, href
}) {
    const router = useRouter();
    return (
        <Box
            // onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
            sx={{ cursor: "pointer" }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    borderRadius: 3, // large (theme.shape.borderRadius * 3)
                    overflow: "hidden",
                    // height: 176,
                    height: 165,
                    boxShadow: "0 10px 24px rgba(0,0,0,.12)", transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    },
                }}
            >
                <Image src={`${process.env.IMAGE_BASE_URL}/${img}`} alt={title} fill style={{ objectFit: "cover" }} />

                {/* bottom readability fade */}
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "44%",
                        background:
                            "linear-gradient(to top, rgba(0,0,0,.55) 0%, rgba(0,0,0,.25) 48%, rgba(0,0,0,0) 100%)",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />

                {/* rating centered at bottom */}
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        bottom: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#fff",
                        textShadow: "0 2px 6px rgba(0,0,0,.5)",
                        fontWeight: 800,
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                >
                    {[...Array(5)].map((_, i) => (
                        <StarRoundedIcon key={i} sx={{ fontSize: 18, color: "#FFC107" }} />
                    ))}
                    <Typography sx={{ ml: 0.5, fontWeight: 800 }}>{`(${reviews})`}</Typography>
                </Box>
            </Paper>

            <Typography sx={{ mt: 1, textAlign: "center", fontWeight: 700, color: "#111" }}>
                {title}
            </Typography>
        </Box>
    );
}
